// My Trading Journey → Am I getting better? (/app/journey/better): skill, not money. Any two
// stretches side by side with a better / worse / same verdict per measure (no verdict on a thin
// sample), the same measures as trends by month, quarter or year, how long every prop account
// lasted, and the lessons from blown accounts. Trading measures include prop accounts: simulated
// money, used here as a measure of skill. The numbers come from journey.js.
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { Lessons, useJourney } from './JourneyStory';
import { featureOpen } from './AppNav';
import { accountHistory } from './business';
import { ENOUGH_TRADES, STRETCH_CHOICES, TURNING_WINDOW, accountLifespans, pendingTurningPoints, turningPoints, compareSkill, periodRange, skillStats, skillTrend, stretchRange } from './journey';
import { formatMoney, tradingDayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { TraderScoreSection } from './ScoreViews';
import { useI18n } from './i18n';

const dayText = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
const monthShort = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' });
const CHOICE_NAMES = {
  first30: 'Your first month', first90: 'Your first 3 months', first180: 'Your first 6 months',
  last30: 'Your last month', last90: 'Your last 3 months', last180: 'Your last 6 months', last365: 'Your last 12 months',
};
const UNITS = [['month', 'Months'], ['quarter', 'Quarters'], ['year', 'Years']];

function show(kind, v, t) {
  if (v === null || v === undefined) return '—';
  if (kind === 'money') return formatMoney(v);
  if (kind === 'cost') return usd.format(Math.round(v)).replace(/\.00$/, '');
  if (kind === 'pct') return `${Math.round(v)}%`;
  if (kind === 'ratio') return v === Infinity ? '∞' : v.toFixed(2);
  if (kind === 'days') return t(Math.round(v) === 1 ? '{n} day' : '{n} days', { n: Math.round(v) });
  return v.toFixed(1);
}

function StretchPick({ label, value, onChange, years }) {
  const { t } = useI18n();
  return (
    <label className="jb-pick">
      <span>{t(label)}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {STRETCH_CHOICES.map((c) => <option key={c} value={c}>{t(CHOICE_NAMES[c])}</option>)}
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </label>
  );
}

// Then vs now: any two stretches, a verdict per measure.
function Compare({ trades, notes, accounts, expenses, firstDay, years, hasProp }) {
  const { t } = useI18n();
  const [thenPick, setThen] = useState('first90');
  const [nowPick, setNow] = useState('last90');
  const r = useMemo(() => {
    const a = stretchRange(thenPick, firstDay);
    const b = stretchRange(nowPick, firstDay);
    const then = skillStats(trades, notes, accounts, expenses, a.from, a.to);
    const now = skillStats(trades, notes, accounts, expenses, b.from, b.to);
    return { then, now, ...compareSkill(then, now), overlap: a.to >= b.from && b.to >= a.from };
  }, [thenPick, nowPick, firstDay, trades, notes, accounts, expenses]);
  const rows = r.rows.filter((x) => hasProp || !['accountLife', 'blownPerMonth', 'passRate', 'feesPerMonth'].includes(x.key));
  const good = r.better >= r.worse;
  return (
    <>
      <section className={`jp-head jb-verdict ${r.judged ? (good ? 'good' : 'bad') : ''}`}>
        <div>
          <h2>{r.judged ? t('Better on {n} of {total}', { n: r.better, total: r.judged }) : t('Not enough to compare yet')}</h2>
          <p>{t('Then: {from} to {to} ({n} trades). Now: {nowFrom} to {nowTo} ({m} trades).', {
            from: dayText(r.then.from), to: dayText(r.then.to), n: r.then.trades, nowFrom: dayText(r.now.from), nowTo: dayText(r.now.to), m: r.now.trades,
          })}</p>
          {r.overlap && <p className="jb-warn">{t('These two stretches overlap, so some trades are in both.')}</p>}
        </div>
        <div className="jb-picks">
          <StretchPick label="Then" value={thenPick} onChange={setThen} years={years} />
          <StretchPick label="Now" value={nowPick} onChange={setNow} years={years} />
        </div>
      </section>
      <section className="jo-card">
        <div className="table-wrap">
          <table className="biz-table jp-table">
            <thead><tr><th>{t('Measure')}</th><th className="num">{t('Then')}</th><th className="num">{t('Now')}</th><th /></tr></thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.key}>
                  <th scope="row">{t(x.label)}</th>
                  <td className="num">{show(x.kind, x.a, t)}</td>
                  <td className={`num ${x.verdict === 'better' ? 'gain' : x.verdict === 'worse' ? 'loss' : ''}`}>{show(x.kind, x.b, t)}</td>
                  <td className="jp-mark">
                    {x.verdict === 'better' ? <span className="gain">▲ {t('Better')}</span>
                      : x.verdict === 'worse' ? <span className="loss">▼ {t('Worse')}</span>
                        : x.verdict === 'same' ? <span className="jb-same">= {t('Same')}</span>
                          : x.verdict === 'thin' ? <span className="jb-thin">{t('Not enough yet')}</span> : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="field-hint">
          {t('Trading measures include your prop accounts: simulated money, counted here as skill, not as money. A verdict needs {n} trades in each stretch, or 3 accounts for the account measures.', { n: ENOUGH_TRADES })}
        </p>
      </section>
    </>
  );
}

// One small bar chart per measure, period by period; thin periods are faded.
function MiniTrend({ title, rows, field, kind, higher, unit }) {
  const { t } = useI18n();
  const [hover, setHover] = useState(null);
  const vals = rows.map((r) => (r[field] === null || r[field] === undefined || r[field] === Infinity ? null : r[field]));
  if (vals.filter((v) => v !== null).length < 2) return null;
  const max = Math.max(...vals.map((v) => Math.abs(v || 0)), 0.0001);
  const neg = vals.some((v) => v < 0);
  const W = 300;
  const H = 110;
  const band = W / rows.length;
  const zero = neg ? H / 2 : H - 4;
  const scale = (neg ? H / 2 - 4 : H - 8) / max;
  const h = hover === null ? null : rows[hover];
  const label = (r) => (unit === 'month' ? monthShort(r.key) : r.key.replace('-', ' '));
  const first = vals.find((v) => v !== null);
  const last = [...vals].reverse().find((v) => v !== null);
  const trendGood = higher === null || first === last ? null : (last > first) === higher;
  return (
    <section className="jo-card jb-mini">
      <div className="jb-mini-head">
        <h3>{t(title)}</h3>
        <span className={trendGood === true ? 'gain' : trendGood === false ? 'loss' : ''}>{h ? `${label(h)}: ${show(kind, vals[hover], t)}` : show(kind, last, t)}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="jb-mini-svg" onMouseLeave={() => setHover(null)} role="img" aria-label={t(title)}>
        <line x1="0" x2={W} y1={zero} y2={zero} className="jo-grid" />
        {rows.map((r, i) => {
          const v = vals[i];
          const bh = v === null ? 0 : Math.max(1.5, Math.abs(v) * scale);
          const bw = Math.max(2, band * 0.7);
          const thin = r.trades < 10 && !['blownPerMonth', 'feesPerMonth', 'accountLife', 'passRate'].includes(field);
          return (
            <g key={r.key} onMouseEnter={() => setHover(i)}>
              <rect x={i * band} y="0" width={band} height={H} className="jo-hit" />
              {v !== null && <rect x={i * band + (band - bw) / 2} y={v >= 0 ? zero - bh : zero} width={bw} height={bh} rx="1.5"
                className={`jb-bar${thin ? ' thin' : ''}${hover === i ? ' hot' : ''}${v < 0 ? ' neg' : ''}`} />}
            </g>
          );
        })}
      </svg>
      <div className="jb-mini-axis"><span>{label(rows[0])}</span><span>{label(rows[rows.length - 1])}</span></div>
    </section>
  );
}

function Trends({ trades, notes, accounts, expenses, range, hasProp }) {
  const { t } = useI18n();
  const [unit, setUnit] = useState('quarter');
  const rows = useMemo(() => skillTrend(trades, notes, accounts, expenses, unit)
    .filter((r) => (!range.from || r.to >= range.from) && (!range.to || r.from <= range.to)), [trades, notes, accounts, expenses, unit, range]);
  const charts = [
    ['Win rate', 'winRate', 'pct', true], ['Profit factor', 'profitFactor', 'ratio', true], ['Average loss', 'avgLoss', 'money', true],
    ['P&L per trading day', 'perDay', 'money', true], ['Trades flagged in Behavior', 'tiltShare', 'pct', false], ['Trading days journaled', 'journaledShare', 'pct', true],
    ...(hasProp ? [['Days a blown account lasted', 'accountLife', 'days', true], ['Accounts blown per month', 'blownPerMonth', 'num', false],
      ['Evaluation pass rate', 'passRate', 'pct', true], ['Firm fees per month (real money)', 'feesPerMonth', 'cost', false]] : []),
  ];
  return (
    <>
      <div className="jo-card-head jb-trends-head">
        <div>
          <h2>{t('Your trends')}</h2>
          <p className="field-hint">{t('Each measure period by period. Faded bars had fewer than 10 trades. Point at a bar for its number.')}</p>
        </div>
        <div className="insights-tabs" role="group" aria-label={t('Show by')}>
          {UNITS.map(([id, label]) => (
            <button key={id} type="button" className={`secondary small${unit === id ? ' active' : ''}`} aria-pressed={unit === id} onClick={() => setUnit(id)}>{t(label)}</button>
          ))}
        </div>
      </div>
      {rows.length < 2 ? <p className="empty">{t('Trends show once you have trades in two or more periods.')}</p> : (
        <div className="jb-minis">
          {charts.map(([title, field, kind, higher]) => <MiniTrend key={field} title={title} rows={rows} field={field} kind={kind} higher={higher} unit={unit} />)}
        </div>
      )}
    </>
  );
}

// How long every prop account lasted, oldest to newest, colored by how it ended.
function Longevity({ accounts, trades, range }) {
  const { t } = useI18n();
  const l = useMemo(() => accountLifespans(accounts, trades), [accounts, trades]);
  const [hover, setHover] = useState(null);
  if (!l) return null;
  const list = l.list.filter((h) => (!range.from || (h.end || h.start) >= range.from) && (!range.to || h.start <= range.to));
  if (list.length < 2) return null;
  const max = Math.max(...list.map((h) => h.days), 1);
  const W = 760;
  const H = 180;
  const band = W / list.length;
  const half = Math.floor(list.length / 2);
  const avg = (xs) => (xs.length ? xs.reduce((s, h) => s + h.days, 0) / xs.length : 0);
  const early = avg(list.slice(0, Math.min(5, half)));
  const recent = avg(list.slice(-Math.min(5, half)));
  const h = hover === null ? null : list[hover];
  const outcome = (x) => (x.outcome === 'passed' ? t('Passed') : x.outcome === 'failed' || x.outcome === 'blown' ? t('Blown') : x.end ? t('Ended') : t('Still going'));
  return (
    <section className="jo-card">
      <h2>{t('How long your prop accounts last')}</h2>
      <p className={`jl-lead ${recent > early ? 'gain' : recent < early ? 'loss' : ''}`}>
        {t(recent >= early ? 'Your latest accounts last {b} on average, up from {a} for your first ones.' : 'Your latest accounts last {b} on average, down from {a} for your first ones.', {
          a: t('{n} days', { n: Math.round(early) }), b: t('{n} days', { n: Math.round(recent) }),
        })}
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="jb-long" onMouseLeave={() => setHover(null)} role="img" aria-label={t('How long your prop accounts last')}>
        <line x1="0" x2={W} y1={H - 2} y2={H - 2} className="jo-grid" />
        {list.map((x, i) => {
          const bh = Math.max(3, (x.days / max) * (H - 12));
          const bw = Math.max(3, Math.min(band * 0.7, 26));
          return (
            <g key={`${x.name}-${x.start}`} onMouseEnter={() => setHover(i)}>
              <rect x={i * band} y="0" width={band} height={H} className="jo-hit" />
              <rect x={i * band + (band - bw) / 2} y={H - 2 - bh} width={bw} height={bh} rx="2" className={`jb-life ${x.outcome}${x.end ? '' : ' active'}${hover === i ? ' hot' : ''}`} />
            </g>
          );
        })}
      </svg>
      <div className="jo-legend">
        <span><i className="jb-key blown" /> {t('Blown')}</span>
        <span><i className="jb-key passed" /> {t('Passed')}</span>
        <span><i className="jb-key active" /> {t('Still going')}</span>
        <span><i className="jb-key ended" /> {t('Ended')}</span>
        {h && <span className="jo-hover">{h.name} · {dayText(h.start)} · {t(h.days === 1 ? '{n} day' : '{n} days', { n: h.days })} · {outcome(h)}</span>}
      </div>
    </section>
  );
}

// What each kind of change is called on a turning point.
const CHANGE_NAMES = {
  rule: 'New rule: {title}', play: 'Added to your Playbook: {title}', retired: 'Retired from your Playbook: {title}', experiment: 'Experiment: {title}',
  moment: 'Moment: {title}', lesson: 'Lesson: {title}', stopped: 'Stopped paying for {title}',
};
const MEASURE_NAMES = {
  accountLife: ['Days a blown account lasted', 'days'], revengePerWeek: ['Revenge trades per week', 'num'], tiltShare: ['Trades flagged in Behavior', 'pct'],
  winRate: ['Win rate', 'pct'], avgLoss: ['Average loss', 'money'], perDay: ['P&L per trading day', 'money'],
};

// The changes the trader made that were followed by a clear change in how they traded.
function TurningPoints({ trades, notes, accounts, expenses, plays, experiments, moments, preview, onLogMoment }) {
  const { t } = useI18n();
  const points = useMemo(() => turningPoints({ trades, notes, accounts, expenses, plays, experiments, moments }), [trades, notes, accounts, expenses, plays, experiments, moments]);
  const waiting = useMemo(() => pendingTurningPoints({ trades, notes, accounts, expenses, plays, experiments, moments }), [trades, notes, accounts, expenses, plays, experiments, moments]);
  const [all, setAll] = useState(false);
  const shown = all ? points : points.slice(0, 4);
  const changeText = (c) => (c.kind === 'size' ? t('Your average size on {symbol} dropped from {a} to {b}', { symbol: c.title, a: c.from.toFixed(1), b: c.to.toFixed(1) })
    : t(CHANGE_NAMES[c.kind], { title: c.title || t('(no title)') }));
  return (
    <section className={`jo-card jb-turning${preview ? ' admin-preview' : ''}`}>
      <div className="jo-card-head">
        <div>
          <h2>{t('Turning points')} {preview && <span className="admin-preview-badge">Admin only</span>}</h2>
          <p className="field-hint">{t('Changes you made (rules, Playbook plays, experiments, moments, lessons, your size) and what happened in the {n} days after, next to the {n} days before.', { n: TURNING_WINDOW })}{' '}
            {t('What you set up in your first two weeks on Market Matrix is your starting point, not a change, and a change needs 3 weeks of trading after it.')}</p>
        </div>
        {onLogMoment && <button type="button" className="secondary small" onClick={onLogMoment}>+ {t('Mark a change')}</button>}
      </div>
      {!points.length && waiting.length > 0 ? null : !points.length ? (
        <p className="empty">{t('No turning point yet: none of your changes was followed by a clear change in how you trade, with enough trades on both sides. Log a moment when you change something, and it shows here once there are trades after it.')}</p>
      ) : (
        <ol className="jb-points">
          {shown.map((p) => (
            <li key={p.date} className={`jb-point ${p.effect}`}>
              <div className="jb-point-head">
                <span className="jb-point-date">{dayText(p.date)}</span>
                <span className={`jb-effect ${p.effect}`}>{t(p.effect === 'helped' ? 'Things got better after this' : 'Things got worse after this')}</span>
              </div>
              <ul className="jb-point-changes">{p.changes.map((c, i) => <li key={i}>{changeText(c)}</li>)}</ul>
              <table className="biz-table jb-point-table">
                <thead><tr><th>{t('Measure')}</th><th className="num">{t('Before')}</th><th className="num">{t('After')}</th></tr></thead>
                <tbody>
                  {p.moves.map((m) => (
                    <tr key={m.key}>
                      <th scope="row">{t(MEASURE_NAMES[m.key][0])}</th>
                      <td className="num">{show(MEASURE_NAMES[m.key][1], m.before, t)}</td>
                      <td className={`num ${m.better ? 'gain' : 'loss'}`}>{m.after > m.before ? '▲' : '▼'} {show(MEASURE_NAMES[m.key][1], m.after, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="jb-point-note">{t('{a} trades before, {b} after. This is what happened after the change, not proof it caused it: markets and other habits change too.', { a: p.before.trades, b: p.after.trades })}</p>
            </li>
          ))}
        </ol>
      )}
      {points.length > 4 && <button type="button" className="secondary small" onClick={() => setAll((v) => !v)}>{all ? t('Show fewer') : t('Show all {n}', { n: points.length })}</button>}
      {waiting.length > 0 && (
        <div className="jb-waiting">
          <h3>{t('Waiting for more trades')}</h3>
          <p className="field-hint">{t('Changes you made that are already being tracked. Each one is judged once there are 3 weeks and {n} trades after it.', { n: ENOUGH_TRADES })}</p>
          <ul>
            {waiting.map((w) => (
              <li key={w.date}>
                <span className="jb-point-date">{dayText(w.date)}</span>
                <span className="jb-waiting-what">{w.changes.map((c) => changeText(c)).join(' · ')}</span>
                <span className="jb-waiting-when">
                  <span className="jo-progress-track"><span style={{ width: `${Math.min(100, (w.after / ENOUGH_TRADES) * 100)}%` }} /></span>
                  <small>{t('{n} of {need} trades after so far · ready around {date}', { n: Math.min(w.after, ENOUGH_TRADES), need: ENOUGH_TRADES, date: dayText(w.readyOn) })}</small>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default function JourneyBetter({ accounts, trades, notes = {}, plays = [], experiments = [], user }) {
  const { t } = useI18n();
  const { scope, openLog, scopeExpenses, allAccounts, story, period = 'all' } = useJourney();
  const turningOpen = featureOpen(user, 'turningPoints');
  const showTurning = turningOpen || Boolean(user && user.isAdmin);
  const [allExpenses, setAllExpenses] = useState([]);
  useEffect(() => { api('/expenses').then(setAllExpenses).catch(() => {}); }, []);
  const expenses = useMemo(() => scopeExpenses(allExpenses), [allExpenses, scope, allAccounts]); // eslint-disable-line react-hooks/exhaustive-deps
  const range = useMemo(() => periodRange(period), [period]);
  const history = useMemo(() => accountHistory(accounts, trades, expenses), [accounts, trades, expenses]);
  const firstDay = useMemo(() => [...trades.map((x) => tradingDayKey(x.date)), ...history.map((h) => h.start)].sort()[0], [trades, history]);
  const years = useMemo(() => [...new Set(trades.map((x) => tradingDayKey(x.date).slice(0, 4)))].sort().reverse(), [trades]);
  const hasProp = scope !== 'personal' && history.length > 0;
  if (!firstDay) return <p className="empty">{t('Am I getting better? starts once you have trades.')}</p>;
  const onEdit = openLog ? (e) => openLog('ended', { accountId: e.accountId }) : null;
  return (
    <div className="jo jb">
      <TraderScoreSection trades={trades} />
      <Compare trades={trades} notes={notes} accounts={accounts} expenses={expenses} firstDay={firstDay} years={years} hasProp={hasProp} />
      {showTurning && <TurningPoints trades={trades} notes={notes} accounts={accounts} expenses={expenses} plays={plays} experiments={experiments}
        moments={(story && story.moments) || []} preview={!turningOpen} onLogMoment={openLog ? () => openLog('moment') : null} />}
      <Trends trades={trades} notes={notes} accounts={accounts} expenses={expenses} range={range} hasProp={hasProp} />
      {hasProp && <Longevity accounts={accounts} trades={trades} range={range} />}
      {hasProp && <Lessons history={history} onEdit={onEdit} />}
    </div>
  );
}
